import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom-v6'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@clouway/ui'

import { Permission, hasPermission, useSession } from '@/features/auth'
import { AccessTab } from '../components/AccessTab'
import { UserForm, type UserFormValues } from '../components/UserForm'
import { membershipViolationCode, type MembershipViolationCode } from '../errors'
import { useRemoveUserMembership, useSetUserActive, useUpdateUser, useUser } from '../hooks'
import { toInput } from '../transform/userTransformer'

export function EditUser() {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: user, isLoading } = useUser(id)
  const { mutateAsync: update } = useUpdateUser()
  const { mutateAsync: setActive } = useSetUserActive()
  const { mutateAsync: removeMembership } = useRemoveUserMembership()
  const { access } = useSession()
  const canReadAccess = hasPermission(access, Permission.roles.list)
  const canWriteAccess = hasPermission(access, Permission.roles.update)
  const canChangeStatus = hasPermission(access, Permission.users.update)
  const canRemoveMembership = hasPermission(access, Permission.users.delete)
  const [removeError, setRemoveError] = useState<MembershipViolationCode | null>(null)

  if (isLoading || !user) return <Skeleton className="mx-auto h-80 w-full max-w-2xl" />

  // `position` isn't a form field (see UserForm) — send back the value this
  // user was fetched with so saving the profile never clobbers it.
  const onSubmit = async (values: UserFormValues) => {
    await update({ id, input: values, position: user.position })
    navigate('/users')
  }

  // Deactivating removes the user from the default (active) list, so there's
  // nothing left on this screen to keep looking at — back to the list.
  // Reactivating keeps the caller on the edit screen (still viewing the same
  // user, now active again).
  const onDeactivate = async () => {
    await setActive({ id, active: false })
    navigate('/users', { replace: true })
  }
  const onActivate = () => setActive({ id, active: true })

  // Unlike deactivation, this detaches the user from the tenant entirely —
  // the server tombstones the membership, so the user disappears from every
  // list state (including "show deactivated"), not just the default one.
  // The two documented business rules (self-detach, last-admin detach) come
  // back as a 400 the caller must see, so the mutation stays on this screen
  // (no navigate) until it either succeeds or the admin cancels.
  const onRemoveMembership = async () => {
    setRemoveError(null)
    try {
      await removeMembership(id)
      navigate('/users', { replace: true })
    } catch (error) {
      setRemoveError(membershipViolationCode(error))
    }
  }

  // Deactivate/activate share one row — only the action available for the
  // user's current status renders. Deactivating stays destructive-styled
  // (still confirmed via AlertDialog); activating is reversible and safe, so
  // it gets a plain outline button with no confirmation.
  const statusRow = canChangeStatus && (
    <DangerZoneRow
      title={t(user.active ? 'user.edit.dangerZone.deactivate.title' : 'user.edit.dangerZone.activate.title')}
      description={t(user.active ? 'user.edit.dangerZone.deactivate.description' : 'user.edit.dangerZone.activate.description')}
    >
      {user.active ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" className="border-destructive/50 text-destructive hover:bg-destructive/10">
              {t('user.edit.deactivate')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('user.edit.deactivate')}</AlertDialogTitle>
              <AlertDialogDescription>{t('user.edit.deactivateDescription')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={onDeactivate}>{tCommon('ui.button.confirm')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : (
        <Button variant="outline" size="sm" onClick={onActivate}>
          {t('user.edit.activate')}
        </Button>
      )}
    </DangerZoneRow>
  )

  // The stronger of the two actions — the user leaves the tenant entirely
  // rather than just losing login access. Any membership violation the
  // server reports (self-detach, last-admin) surfaces inline in this row.
  const removeRow = canRemoveMembership && (
    <DangerZoneRow
      title={t('user.edit.dangerZone.remove.title')}
      description={t('user.edit.remove.description')}
      alert={
        removeError && (
          <Alert variant="destructive">
            <AlertDescription>{t(`user.edit.remove.errors.${removeError}`)}</AlertDescription>
          </Alert>
        )
      }
    >
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline" size="sm" className="border-destructive/50 text-destructive hover:bg-destructive/10">
            {t('user.edit.remove.action')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('user.edit.remove.action')}</AlertDialogTitle>
            <AlertDialogDescription>{t('user.edit.remove.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onRemoveMembership}>{tCommon('ui.button.confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DangerZoneRow>
  )

  // Hidden entirely unless the caller can do at least one of the two
  // actions — an empty danger zone is just noise.
  const dangerZone = (canChangeStatus || canRemoveMembership) && (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="text-destructive text-sm">{t('user.edit.dangerZone.title')}</CardTitle>
      </CardHeader>
      <CardContent className="divide-y">
        {statusRow}
        {removeRow}
      </CardContent>
    </Card>
  )

  const profile = (
    <div className="flex flex-col gap-4">
      <UserForm mode="edit" initialValues={toInput(user)} onSubmit={onSubmit} />
      {dangerZone}
    </div>
  )

  // No accounts.roles.list: the caller cannot use the Access tab at all, so
  // skip the tab chrome entirely rather than show a tab that 401s.
  if (!canReadAccess) {
    return profile
  }

  return (
    <Tabs defaultValue="profile" className="mx-auto w-full max-w-2xl">
      <TabsList>
        <TabsTrigger value="profile">{t('user.edit.tabs.profile')}</TabsTrigger>
        <TabsTrigger value="access">{t('user.edit.tabs.access')}</TabsTrigger>
      </TabsList>
      <TabsContent value="profile">{profile}</TabsContent>
      <TabsContent value="access">
        <AccessTab userId={id} canWrite={canWriteAccess} />
      </TabsContent>
    </Tabs>
  )
}

// One row of the danger zone: a title + one-line description on the left,
// the confirming action on the right. `alert` renders below the description
// (not next to the button) so error copy reads as belonging to the action
// being explained, not as decoration on the trigger.
function DangerZoneRow({
  title,
  description,
  alert,
  children,
}: {
  title: string
  description: string
  alert?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0">
      <div className="flex flex-col gap-2">
        <div>
          <div className="text-sm font-medium">{title}</div>
          <div className="text-muted-foreground text-xs">{description}</div>
        </div>
        {alert}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  )
}
